import React, { useState } from 'react';
import axios from 'axios';
import { PageHeader, Section } from '../components/PageShell';
import { Mail, MapPin, Phone, ArrowUpRight, CheckCircle2, Calendar, Linkedin, Github } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const budgets = ['< $25k', '$25k – $75k', '$75k – $250k', '$250k+'];
const services = [
  'Enterprise AI', 'Generative AI', 'AI Agents', 'Custom Software Development',
  'Enterprise SaaS', 'ERP Development', 'CRM Development', 'Web Development',
  'Mobile App Development', 'Cloud Engineering', 'DevOps', 'Business Intelligence',
  'Automation', 'Data Analytics', 'UI/UX Design', 'IT Consulting', 'Not sure yet',
];

export default function Contact() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    budget: '',
    message: '',
  });
  const [status, setStatus] = useState({ state: 'idle', error: '' });

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: 'submitting', error: '' });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ state: 'success', error: '' });
      setForm({ name: '', email: '', company: '', service: '', budget: '', message: '' });
    } catch (err) {
      setStatus({
        state: 'error',
        error: err?.response?.data?.detail || 'Something went wrong. Please try again.',
      });
    }
  };

  return (
    <div data-testid="contact-page" className="pb-24">
      <PageHeader
        eyebrow="Contact"
        title={<>Let&rsquo;s build something <span className="font-serif">extraordinary.</span></>}
        subtitle="Tell us about your project. A senior partner from YP Nova will get back within one business day."
      />

      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Info panel */}
          <aside className="lg:col-span-2 rounded-[32px] bg-gradient-to-br from-[#051A24] via-[#1E1B4B] to-[#4C1D95] text-white p-8 md:p-10 shadow-[0_20px_60px_rgba(76,29,149,0.25)] flex flex-col justify-between">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl mb-4">Talk to a human.</h2>
              <p className="text-[#E0EBF0] leading-relaxed mb-8">
                A senior partner reads every message. No forms disappearing into a black hole.
              </p>

              <div className="flex flex-col gap-5 text-[#E0EBF0]">
                <a href="mailto:hello@ypnova.example.com" className="flex items-center gap-3 hover:text-white" data-testid="contact-email">
                  <Mail className="w-4 h-4" /> hello@ypnova.example.com
                </a>
                <a href="tel:+10000000000" className="flex items-center gap-3 hover:text-white" data-testid="contact-phone">
                  <Phone className="w-4 h-4" /> +1 (000) 000-0000
                </a>
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4" /> International · Remote-first
                </div>
              </div>
            </div>

            <div className="mt-10 flex flex-col gap-5">
              <a
                href="https://calendly.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-calendly"
                className="inline-flex items-center gap-2 bg-white text-[#051A24] rounded-full px-5 py-3 text-sm font-medium hover:opacity-95"
              >
                <Calendar className="w-4 h-4" /> Book on Calendly
              </a>

              <div className="flex gap-4 pt-2">
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#E0EBF0] hover:text-white" data-testid="contact-linkedin">
                  <Linkedin className="w-4 h-4" /> LinkedIn
                </a>
                <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#E0EBF0] hover:text-white" data-testid="contact-github">
                  <Github className="w-4 h-4" /> GitHub
                </a>
              </div>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-3 rounded-[32px] bg-white border border-[#7C3AED]/12 p-8 md:p-10 shadow-[0_4px_16px_rgba(0,0,0,0.04)]">
            {status.state === 'success' ? (
              <div data-testid="contact-success" className="flex flex-col items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#051A24] text-white flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-3xl text-[#0D212C]">Message received.</h3>
                <p className="text-[#273C46] max-w-md">
                  Thanks &mdash; we&rsquo;ve got your message and a partner will get back to you within one business day.
                </p>
                <button
                  onClick={() => setStatus({ state: 'idle', error: '' })}
                  className="mt-2 inline-flex items-center gap-1 text-[#051A24] underline underline-offset-4"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <label className="flex flex-col gap-2 md:col-span-1">
                  <span className="text-xs font-mono uppercase tracking-widest text-[#273C46]">Your name</span>
                  <input
                    required
                    data-testid="contact-name"
                    type="text"
                    value={form.name}
                    onChange={update('name')}
                    className="px-4 py-3 rounded-2xl border border-[#051A24]/15 bg-white text-[#051A24] focus:outline-none focus:border-[#051A24]"
                    placeholder="Alex Kim"
                  />
                </label>

                <label className="flex flex-col gap-2 md:col-span-1">
                  <span className="text-xs font-mono uppercase tracking-widest text-[#273C46]">Work email</span>
                  <input
                    required
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={update('email')}
                    className="px-4 py-3 rounded-2xl border border-[#051A24]/15 bg-white text-[#051A24] focus:outline-none focus:border-[#051A24]"
                    placeholder="alex@company.com"
                  />
                </label>

                <label className="flex flex-col gap-2 md:col-span-2">
                  <span className="text-xs font-mono uppercase tracking-widest text-[#273C46]">Company</span>
                  <input
                    data-testid="contact-company"
                    type="text"
                    value={form.company}
                    onChange={update('company')}
                    className="px-4 py-3 rounded-2xl border border-[#051A24]/15 bg-white text-[#051A24] focus:outline-none focus:border-[#051A24]"
                    placeholder="Acme Inc."
                  />
                </label>

                <label className="flex flex-col gap-2 md:col-span-1">
                  <span className="text-xs font-mono uppercase tracking-widest text-[#273C46]">Service of interest</span>
                  <select
                    data-testid="contact-service"
                    value={form.service}
                    onChange={update('service')}
                    className="px-4 py-3 rounded-2xl border border-[#051A24]/15 bg-white text-[#051A24] focus:outline-none focus:border-[#051A24]"
                  >
                    <option value="">Select…</option>
                    {services.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-2 md:col-span-1">
                  <span className="text-xs font-mono uppercase tracking-widest text-[#273C46]">Budget</span>
                  <select
                    data-testid="contact-budget"
                    value={form.budget}
                    onChange={update('budget')}
                    className="px-4 py-3 rounded-2xl border border-[#051A24]/15 bg-white text-[#051A24] focus:outline-none focus:border-[#051A24]"
                  >
                    <option value="">Select…</option>
                    {budgets.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-2 md:col-span-2">
                  <span className="text-xs font-mono uppercase tracking-widest text-[#273C46]">Project brief</span>
                  <textarea
                    required
                    data-testid="contact-message"
                    value={form.message}
                    onChange={update('message')}
                    rows={5}
                    className="px-4 py-3 rounded-2xl border border-[#051A24]/15 bg-white text-[#051A24] focus:outline-none focus:border-[#051A24] resize-none"
                    placeholder="What are you building? What are you trying to change?"
                  />
                </label>

                {status.state === 'error' && (
                  <p data-testid="contact-error" className="md:col-span-2 text-sm text-red-600">
                    {status.error}
                  </p>
                )}

                <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <p className="text-xs text-[#273C46]">
                    By submitting you agree to be contacted about your enquiry.
                  </p>
                  <button
                    type="submit"
                    disabled={status.state === 'submitting'}
                    data-testid="contact-submit"
                    className="inline-flex items-center gap-2 bg-[#051A24] text-white rounded-full px-7 py-3 shadow-[0_4px_20px_rgba(5,26,36,0.15)] hover:opacity-95 disabled:opacity-60"
                  >
                    {status.state === 'submitting' ? 'Sending…' : 'Send message'}
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </Section>

      {/* Map */}
      <Section>
        <div className="rounded-[32px] overflow-hidden border border-[#7C3AED]/12 shadow-[0_4px_16px_rgba(0,0,0,0.04)]">
          <iframe
            title="YP Nova on Google Maps"
            data-testid="contact-map"
            src="https://www.google.com/maps?q=San+Francisco,+CA&output=embed"
            className="w-full h-[360px] md:h-[440px]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </Section>
    </div>
  );
}
